import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import EmptyState from "@/components/base/EmptyState";
import { useSeo } from "@/hooks/useSeo";
import { newsCategories, newsList } from "@/mocks/news";

const PAGE_SIZE = 6;

export default function News() {
  useSeo({
    title: "动漫资讯 - 樱花动漫官网",
    description:
      "樱花动漫官网资讯频道，提供最新新番片单、行业观察、动画评测与线下活动报道，每日更新动漫圈动态。",
    keywords: "动漫资讯,新番片单,动画评测,动漫活动,樱花动漫官网",
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const activeCategory = searchParams.get("category") || "全部";

  const filtered = useMemo(() => {
    if (activeCategory === "全部") return newsList;
    return newsList.filter((item) => item.category === activeCategory);
  }, [activeCategory]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [activeCategory]);

  const visible = filtered.slice(0, visibleCount);
  const hotTags = ["新番", "评测", "声优", "国产动画", "配乐", "取景地", "片单", "幕后"];

  const setCategory = (category: string) => {
    const next = new URLSearchParams(searchParams);
    if (category === "全部") next.delete("category");
    else next.set("category", category);
    setSearchParams(next);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-8 md:py-12">
            <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
              <span className="text-foreground-800">动漫资讯</span>
            </nav>
            <h1 className="mt-4 text-2xl font-bold text-foreground-950 md:text-3xl">动漫资讯</h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
              新番片单、行业观察、深度评测与线下活动。编辑部每天整理圈内动态，帮你在信息过载的时代里，只看值得看的那几条。
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-newspaper-line text-primary-500" />
                共 {newsList.length} 篇内容
              </span>
              <span className="text-background-400">/</span>
              <span>每日 09:00 与 18:00 更新</span>
            </div>
          </div>
        </section>

        <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8">
          <div className="flex flex-col gap-2 border-b border-background-200 pb-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="scrollbar-hide flex gap-1 overflow-x-auto">
              {newsCategories.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setCategory(category)}
                  className={[
                    "whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors",
                    activeCategory === category
                      ? "bg-primary-500 font-medium text-background-50"
                      : "text-foreground-700 hover:bg-background-100",
                  ].join(" ")}
                >
                  {category}
                </button>
              ))}
            </div>
            <span className="shrink-0 text-xs text-foreground-500">
              当前栏目：{activeCategory} · {filtered.length} 篇
            </span>
          </div>

          <div className="mt-7 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px]">
            <div>
              {visible.length > 0 ? (
                <>
                  <div className="space-y-5">
                    {visible.map((item) => (
                      <article
                        key={item.id}
                        className="group overflow-hidden rounded-lg border border-background-200 bg-background-50"
                      >
                        <Link to={`/news/${item.id}`} className="flex flex-col sm:flex-row">
                          <div className="relative aspect-[16/9] w-full shrink-0 overflow-hidden bg-background-200 sm:aspect-auto sm:h-[196px] sm:w-[280px]">
                            <img
                              src={item.cover}
                              alt={`${item.title} 资讯封面`}
                              title={`${item.title} - 动漫资讯`}
                              className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-[1.03]"
                            />
                          </div>
                          <div className="flex min-w-0 flex-1 flex-col p-4 md:p-5">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="rounded bg-primary-100 px-2 py-0.5 font-label text-[10px] text-primary-700">
                                {item.category}
                              </span>
                              <span className="font-label text-[11px] text-foreground-500">{item.date}</span>
                              <span className="flex items-center gap-1 text-[11px] text-foreground-500">
                                <i className="ri-eye-line" />
                                {item.views}
                              </span>
                            </div>
                            <h2 className="mt-2 line-clamp-2 text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg">
                              {item.title}
                            </h2>
                            <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{item.excerpt}</p>
                            <div className="mt-auto flex items-center justify-between pt-3">
                              <span className="flex items-center gap-1 text-xs text-foreground-500">
                                <i className="ri-user-3-line" />
                                {item.author}
                              </span>
                              <span className="flex items-center gap-1 whitespace-nowrap text-xs text-primary-600">
                                阅读全文
                                <i className="ri-arrow-right-line" />
                              </span>
                            </div>
                          </div>
                        </Link>
                      </article>
                    ))}
                  </div>

                  <div className="mt-8 flex flex-col items-center gap-3">
                    {visible.length < filtered.length ? (
                      <button
                        type="button"
                        onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
                        className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                      >
                        加载更多资讯
                        <i className="ri-arrow-down-line text-sm" />
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-4 py-2 text-xs text-foreground-500">
                        <i className="ri-check-line" />
                        本栏目内容已全部加载
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <EmptyState
                  title="这个栏目暂时还没有内容"
                  description="编辑部正在准备新的选题，你可以先看看其他栏目，或者回到全部资讯浏览。"
                  actionLabel="查看全部资讯"
                  onAction={() => setCategory("全部")}
                  secondaryLabel="回到首页"
                  secondaryTo="/"
                />
              )}
            </div>

            <aside className="space-y-6">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="text-base font-bold text-foreground-950">热门标签</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {hotTags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/search?q=${encodeURIComponent(tag)}&type=news`}
                      className="whitespace-nowrap rounded-md bg-background-50 px-2.5 py-1 text-xs text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
                    >
                      # {tag}
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="text-base font-bold text-foreground-950">编辑推荐</h2>
                <ul className="mt-3 space-y-3">
                  {newsList.slice(1, 5).map((item) => (
                    <li key={item.id}>
                      <Link to={`/news/${item.id}`} className="group flex gap-3">
                        <div className="h-14 w-20 shrink-0 overflow-hidden rounded-md bg-background-200">
                          <img
                            src={item.cover}
                            alt={`${item.title} 封面`}
                            className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <div className="min-w-0">
                          <h3 className="line-clamp-2 text-xs leading-snug text-foreground-800 transition-colors group-hover:text-primary-600">
                            {item.title}
                          </h3>
                          <span className="mt-1 block font-label text-[10px] text-foreground-500">{item.date}</span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg bg-primary-500 p-5 text-background-50">
                <h2 className="text-base font-bold">想投稿？</h2>
                <p className="mt-1.5 text-xs leading-relaxed text-primary-50/90">
                  我们长期接受动漫评测与专题投稿，欢迎把你的观点写下来发给我们。
                </p>
                <Link
                  to="/contact"
                  className="mt-4 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-background-50 px-3.5 py-2 text-xs font-medium text-primary-700 transition-colors hover:bg-background-100"
                >
                  联系编辑部
                  <i className="ri-arrow-right-line text-xs" />
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}